// Financial Chart of Accounts TypeScript interfaces
// Based on the new database schema

export type AccountNormalBalance = 'debit' | 'credit';

// Accounts Payable status types
export type APInvoiceStatus = 'unpaid' | 'partially_paid' | 'paid' | 'overdue';

// Accounts Receivable status types
export type ARInvoiceStatus = 'draft' | 'sent' | 'overdue' | 'partially_paid' | 'paid' | 'cancelled';

export interface FinancialAccountCategory {
  id: string;
  category_name: string;
  category_code: string;
  normal_balance: AccountNormalBalance;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FinancialAccountType {
  id: string;
  category_id: string;
  type_name: string;
  type_code: string;
  description?: string;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Populated via joins
  category?: FinancialAccountCategory;
}

export interface FinancialAccountSubtype {
  id: string;
  type_id: string;
  subtype_name: string;
  subtype_code: string;
  /** ISO country code (e.g. `FR`); NULL = universal. */
  jurisdiction: string | null;
  /** The type's general ("Other …") subtype — the default when none is picked. */
  is_general: boolean;
  /** Set on the subtype a system account (AR, input VAT, …) is classified under. */
  system_account_type?: SystemAccountType | null;
  /** 4-digit base that new account numbers are allocated from (docs/CHART_OF_ACCOUNTS.md). */
  house_code: string | null;
  description?: string;
  /** Terms the CoA template mapper matches account names against. */
  match_keywords?: string[];
  /**
   * Set only on a CONTRA subtype (owner draws, accumulated depreciation, …):
   * the side its accounts take, opposite the category's. NULL = the
   * category's (docs/CHART_OF_ACCOUNTS.md "Contra subtypes").
   */
  normal_balance?: 'debit' | 'credit' | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Populated via joins
  type?: FinancialAccountType;
  /** Reference-scheme codes mapped to this subtype (RGS, PCG, SKR, …). */
  reference_codes?: AccountSubtypeReferenceCode[];
}

export interface AccountSubtypeReferenceCode {
  scheme_code: string;
  code: string;
  code_label: string | null;
}

/** A national reference chart (RGS, PCG, SKR 03, Form 990, …) — migration-only data. */
export interface AccountReferenceScheme {
  code: string;
  /** ISO country code. */
  jurisdiction: string;
  name: string;
  code_pattern: string;
  version: string;
  /** Calendar day the source was current (`YYYY-MM-DD`). */
  as_of: string;
  source_url: string;
}

export type SystemAccountType =
  | 'fx_clearing'
  | 'suspense'
  | 'retained_earnings'
  | 'accounts_receivable'
  | 'accounts_payable'
  | 'input_vat'
  | 'output_vat'
  | 'vat_settlement'
  | 'fringe_benefit_payable'
  | 'sales_tax_payable'
  | 'meals_rooms_tax_payable'
  | 'opening_balance_equity';

export interface ChartOfAccount {
  id: string;
  organization_id: string;
  account_number: string;
  account_name: string;
  account_description?: string;

  // Account categorization
  category_id: string;
  type_id: string;
  subtype_id?: string;

  // Hierarchy support
  parent_account_id?: string;
  account_level: number;
  full_account_path?: string; // Computed path like "1000.1010.1011"

  // Account properties
  normal_balance: AccountNormalBalance;
  is_active: boolean;
  is_system_account: boolean;
  system_account_type?: SystemAccountType | null;
  can_have_children: boolean;
  requires_project: boolean;

  // Financial properties
  opening_balance: number;
  opening_balance_date?: string | null; // ISO date string — the opening journal's date (as-of reports before it exclude the balance)
  current_balance: number;
  default_tax_rate_id?: string | null; // FK to tax_rates table
  currency?: string | null; // ISO 4217 currency code, null means use org default

  // Metadata
  tags?: string[];
  custom_fields?: Record<string, unknown>;

  // Audit fields
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;

  // Populated via joins
  category?: FinancialAccountCategory;
  type?: FinancialAccountType;
  subtype?: FinancialAccountSubtype;
  default_tax_rate?: { id: string; name: string; rate: number; direction: string } | null;
  parent_account?: ChartOfAccount;
  child_accounts?: ChartOfAccount[];
}

export interface AccountNumberPool {
  id: string;
  organization_id: string;
  category_id: string;
  type_id?: string;
  subtype_id?: string;
  next_number: number;
  number_format: string; // Template like '{prefix}{number}' or just '{number}'
  prefix?: string;
  suffix?: string;
  pad_length: number;
  created_at: string;
  updated_at: string;
  // Populated via joins
  category?: FinancialAccountCategory;
  type?: FinancialAccountType;
  subtype?: FinancialAccountSubtype;
}

// Form data interfaces for creating/updating accounts
export interface CreateAccountData {
  account_name: string;
  account_description?: string;
  category_id: string;
  type_id: string;
  subtype_id?: string;
  parent_account_id?: string;
  normal_balance: AccountNormalBalance;
  can_have_children?: boolean;
  requires_project?: boolean;
  opening_balance?: number;
  opening_balance_date?: string | null; // ISO date string — the opening journal's date (as-of reports before it exclude the balance)
  default_tax_rate_id?: string | null;
  currency?: string | null; // ISO 4217 currency code, null means use org default
  tags?: string[];
  custom_fields?: Record<string, unknown>;
}

// Classification on edit: type_id / subtype_id / parent_account_id route through
// the update_account_classification RPC (category + normal_balance stay locked).
// Explicit `null` clears the subtype / makes the account top-level.
export interface UpdateAccountData
  extends Omit<Partial<CreateAccountData>, 'subtype_id' | 'parent_account_id'> {
  subtype_id?: string | null;
  parent_account_id?: string | null;
  is_active?: boolean;
  change_reason?: string;
  /** Answer to a 409 NORMAL_BALANCE_CHOICE_REQUIRED: keep a manual override, or reset to the standard side. */
  normal_balance_choice?: 'keep' | 'reset';
}

/** Coded refusals of update_account_classification (API 400 `{ error, code }`). */
export const ACCOUNT_CLASSIFICATION_ERROR_CODES = [
  'SYSTEM_ACCOUNT',
  'OBE_ACCOUNT',
  'OBE_TYPE',
  'PARENT_NOT_FOUND',
  'PARENT_CANNOT_HAVE_CHILDREN',
  'PARENT_CATEGORY_MISMATCH',
  'PARENT_TYPE_MISMATCH',
  'CYCLE',
  'TYPE_REQUIRED',
  'TYPE_CATEGORY_MISMATCH',
  'SUBTYPE_MISMATCH',
  'CHILD_SUBTYPE_NOT_CONTRA',
  'CONTRA_SUB_ACCOUNTS_BLOCK',
  'DEPTH_EXCEEDED',
  'FEED_LINKED',
] as const;
export type AccountClassificationErrorCode = (typeof ACCOUNT_CLASSIFICATION_ERROR_CODES)[number];

/** Which form field a classification refusal belongs to. */
export const ACCOUNT_CLASSIFICATION_ERROR_FIELD: Record<
  AccountClassificationErrorCode,
  'type_id' | 'subtype_id' | 'parent_account_id'
> = {
  SYSTEM_ACCOUNT: 'type_id',
  OBE_ACCOUNT: 'type_id',
  OBE_TYPE: 'type_id',
  TYPE_REQUIRED: 'type_id',
  TYPE_CATEGORY_MISMATCH: 'type_id',
  SUBTYPE_MISMATCH: 'type_id',
  // A sub-account's subtype is its parent's or a contra subtype of that type.
  CHILD_SUBTYPE_NOT_CONTRA: 'subtype_id',
  // The change would drop sub-accounts' own contra subtype — the envelope's
  // `accounts` (ContraSubAccountBlock[]) are listed in the message.
  CONTRA_SUB_ACCOUNTS_BLOCK: 'type_id',
  FEED_LINKED: 'type_id',
  PARENT_NOT_FOUND: 'parent_account_id',
  PARENT_CANNOT_HAVE_CHILDREN: 'parent_account_id',
  PARENT_CATEGORY_MISMATCH: 'parent_account_id',
  PARENT_TYPE_MISMATCH: 'parent_account_id',
  CYCLE: 'parent_account_id',
  DEPTH_EXCEEDED: 'parent_account_id',
};

// Hierarchical account structure for display
export interface HierarchicalAccount extends ChartOfAccount {
  children?: HierarchicalAccount[];
  depth: number;
  hasParent: boolean;
  hasChildren: boolean;
}

// Account summary for reports
export interface AccountSummary {
  account_id: string;
  account_number: string;
  account_name: string;
  account_type: string;
  normal_balance: AccountNormalBalance;
  opening_balance: number;
  current_balance: number;
  debit_total: number;
  credit_total: number;
  net_change: number;
  parent_account_id?: string;
  category_name: string;
  type_name: string;
  subtype_name?: string;
}

// Account validation result
export interface AccountValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Account auto-numbering options
export interface AutoNumberingOptions {
  category_id: string;
  type_id?: string;
  subtype_id?: string;
  use_prefix?: boolean;
  use_suffix?: boolean;
  custom_format?: string;
}

// Account tree node for hierarchical display
export interface AccountTreeNode {
  account: ChartOfAccount;
  children: AccountTreeNode[];
  level: number;
  isExpanded?: boolean;
  isSelected?: boolean;
}

// Financial statement line item
export interface FinancialStatementItem {
  account_id: string;
  account_number: string;
  account_name: string;
  amount: number;
  parent_account_id?: string;
  is_parent: boolean;
  children?: FinancialStatementItem[];
  level: number;
}

// Account search and filter options
export interface AccountSearchOptions {
  search_term?: string;
  category_ids?: string[];
  type_ids?: string[];
  subtype_ids?: string[];
  is_active?: boolean;
  has_balance?: boolean;
  parent_account_id?: string | null;
  include_children?: boolean;
  sort_by?: 'account_number' | 'account_name' | 'created_at' | 'balance';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

// Bulk account operations
export interface BulkAccountOperation {
  operation: 'activate' | 'deactivate' | 'delete' | 'update';
  account_ids: string[];
  update_data?: Partial<UpdateAccountData>;
  change_reason?: string;
}

export interface BulkOperationResult {
  success_count: number;
  error_count: number;
  errors: Array<{
    account_id: string;
    error: string;
  }>;
}